import { Link } from "react-router";
import { DuviNicheSiteBrandMark } from "@/components/duvi-niche-site-brand-mark";

export interface DuviNicheSiteFooterLink {
  label: string;
  /** Same-site route. Mutually exclusive with `href`. */
  to?: string;
  /** Absolute or placeholder destination. Mutually exclusive with `to`. */
  href?: string;
}

export interface DuviNicheSiteFooterColumn {
  title: string;
  links: DuviNicheSiteFooterLink[];
}

export interface DuviNicheSiteFooterProps {
  blurb: string;
  columns: DuviNicheSiteFooterColumn[];
  copyright: string;
}

const LINK_CLASSES =
  "font-[family-name:var(--font-body)] text-[14px] text-[color:var(--dark-heading)] no-underline transition-colors duration-[var(--duration-fast)] hover:text-[color:var(--dark-brand)] hover:underline";

/**
 * DuviNicheSiteFooter — ported from the bespoke Fishwalkers design system's
 * `components/marketing/SiteFooter.jsx`
 * (duvi-design/exports/niches/fishwalkers/system.zip): the dark band set in
 * the heading ink, with the lock-up and blurb in a wide first column, link
 * columns beside it, and a copyright rule beneath.
 *
 * This is the one place on the site that reads the `--dark-*` inks. They are
 * not a theme and there is no toggle — the band's ground is `--heading`, so
 * its text has to come from the forced-dark set to stay AA against it.
 *
 * The export's columns are flat label lists (a prototype has nowhere to
 * navigate to); here each entry carries its real destination, and only the
 * links this site can actually honour are rendered.
 */
export function DuviNicheSiteFooter({
  blurb,
  columns,
  copyright,
}: DuviNicheSiteFooterProps) {
  return (
    <footer className="w-full bg-[var(--heading)] py-[var(--space-16)]">
      <div className="mx-auto w-full max-w-[var(--container-max)] px-[var(--container-pad)] lg:px-[var(--container-pad-lg)]">
        <div className="grid grid-cols-1 gap-[var(--space-10)] md:grid-cols-[1.4fr_repeat(3,1fr)]">
          <div className="flex flex-col gap-[var(--space-4)]">
            <DuviNicheSiteBrandMark size={32} tone="dark" to="/" />
            <p className="m-0 max-w-[38ch] font-[family-name:var(--font-body)] text-[14px] leading-[var(--leading-body)] text-[color:var(--dark-body)]">
              {blurb}
            </p>
          </div>
          {columns.map((column) => (
            <nav
              key={column.title}
              aria-label={column.title}
              className="flex flex-col gap-[var(--space-3)]"
            >
              <h4 className="font-[family-name:var(--font-body)] text-[13px] font-semibold uppercase tracking-[var(--tracking-eyebrow)] text-[color:var(--dark-muted)]">
                {column.title}
              </h4>
              <ul className="m-0 flex list-none flex-col gap-[var(--space-2)] p-0">
                {column.links.map((link) => (
                  <li key={link.label}>
                    {link.to ? (
                      <Link to={link.to} className={LINK_CLASSES}>
                        {link.label}
                      </Link>
                    ) : (
                      <a href={link.href ?? "#"} className={LINK_CLASSES}>
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div className="mt-[var(--space-12)] border-t border-[var(--dark-border)] pt-[var(--space-6)]">
          <p className="m-0 font-[family-name:var(--font-body)] text-[13px] text-[color:var(--dark-muted)]">
            {copyright}
          </p>
        </div>
      </div>
    </footer>
  );
}
